// @repletoryreach/sdk-utils/whatsapp-text: finding the links in a message body.
//
// This runs BEFORE any formatting marker is looked at, and that ordering is the
// whole point. A real link from Meta looks like this:
//
//   https://business.facebook.com/latest/whatsapp%5Fmanager/setup%5Fguidance?nav%5Fref=nudge&lang=en_US
//
// Meta percent-encodes the underscores because WhatsApp itself would otherwise
// read `_manager/setup_` as italics. If we applied `_italic_` first we would
// reproduce that bug on `lang=en_US` and on any link a customer pastes. So a
// link is matched first and handed onward as one indivisible atom that the
// emphasis pass can never look inside.

/**
 * The suffixes a bare host is allowed to end in. A curated list, not "any two
 * letters", because the common failure of a naive linkifier is a sentence with
 * no space after the full stop: "…track progress.Get detailed instructions"
 * must not become a link to the `.Get` domain. Two guards do that: the suffix
 * has to be one of these, and it has to be lowercase as written.
 */
const TLDS = [
  'com', 'org', 'net', 'edu', 'gov', 'int', 'mil', 'info', 'biz', 'name', 'pro',
  'app', 'dev', 'io', 'ai', 'co', 'me', 'tv', 'cc', 'ly', 'sh', 'gg', 'xyz',
  'shop', 'store', 'online', 'site', 'tech', 'cloud', 'email', 'link', 'live',
  'life', 'blog', 'page', 'news', 'media', 'agency', 'studio', 'design', 'chat',
  'africa', 'travel', 'mobi', 'asia', 'eu', 'uk', 'us', 'za', 'ng', 'ke', 'gh',
  'tz', 'ug', 'zm', 'zw', 'bw', 'na', 'mz', 'ls', 'sz', 'au', 'nz', 'ca', 'de',
  'fr', 'nl', 'es', 'it', 'pt', 'se', 'no', 'dk', 'fi', 'pl', 'ch', 'at', 'be',
  'ie', 'in', 'pk', 'bd', 'sg', 'my', 'id', 'ph', 'th', 'vn', 'jp', 'kr', 'cn',
  'hk', 'tw', 'br', 'ar', 'cl', 'mx', 'ae', 'sa', 'il', 'tr', 'ru', 'ua'
].join('|');

const LABEL = '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?';
/** Anything a URL may carry after the host. Whitespace and quotes end it. */
const REST = '(?:[:/?#][^\\s<>"\'`\\u0000-\\u001f]*)?';

/**
 * One pass, four shapes, leftmost match wins. Deliberately not case-insensitive
 * as a whole: the scheme spells its own letters out so `HTTPS://` still matches,
 * whilst a bare host's suffix stays lowercase-only for the reason above.
 */
const LINK = new RegExp(
  // http(s)://…
  '(?<![A-Za-z0-9@])(?<url>[Hh][Tt][Tt][Pp][Ss]?://[^\\s<>"\'`\\u0000-\\u001f]+)' +
  // someone@example.co.za
  `|(?<![A-Za-z0-9@.+_-])(?<email>[A-Za-z0-9._%+-]+@(?:${LABEL}\\.)+(?:${TLDS})(?![A-Za-z0-9-]))` +
  // wa.me/27615360809, repletoryreach.co.za/pricing
  `|(?<![A-Za-z0-9@./-])(?<host>(?:${LABEL}\\.)+(?:${TLDS})(?![A-Za-z0-9-])(?!\\.[A-Za-z0-9])${REST})` +
  // +27 61 536 0809, already in international form
  '|(?<![A-Za-z0-9+])(?<phone>\\+\\d(?:[ \\t.()-]?\\d){7,14})(?![0-9])',
  'g'
);

export type LinkKind = 'url' | 'email' | 'phone';

export interface LinkMatch {
  kind: LinkKind;
  /** Where the link starts in the source string. */
  index: number;
  /** Exactly the characters matched, so the caller can skip past them. */
  raw: string;
  /** Where the browser goes. Always http, https, mailto or tel. */
  href: string;
  /** What the reader sees, shortened when the URL is long. */
  text: string;
  /** The full target, for the title attribute. */
  title: string;
}

/** Characters that end a sentence rather than a URL. */
const TRAILING = '.,;:!?\'"«»';

/**
 * Drops the punctuation a sentence left on the end of a URL. A closing bracket
 * survives only when the URL opened one, so a reference link keeps its `)` and
 * "(see example.com/a)" does not.
 */
function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  while (end > 0) {
    const c = url[end - 1];
    if (TRAILING.includes(c)) { end -= 1; continue; }
    const open = c === ')' ? '(' : c === ']' ? '[' : c === '}' ? '{' : '';
    if (open) {
      const head = url.slice(0, end);
      const opens = head.split(open).length - 1;
      const closes = head.split(c).length - 1;
      if (closes > opens) { end -= 1; continue; }
    }
    break;
  }
  return url.slice(0, end);
}

/**
 * Control characters and the bidi overrides, which can make a link read as one
 * address and go to another. Stripped from both the href and the label.
 */
const UNSAFE_CHARS = /[\u0000-\u001f\u007f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g;

/** Percent escapes that always mean the character itself, so decoding them changes the reading and nothing else. */
function decodeUnreserved(value: string): string {
  return value.replace(/%[0-9A-Fa-f]{2}/g, (m) => {
    const c = String.fromCharCode(parseInt(m.slice(1), 16));
    return /[A-Za-z0-9\-._~]/.test(c) ? c : m;
  });
}

/**
 * A long URL cannot be allowed to set the width of a chat bubble, so the label
 * is the host plus as much of the path as fits on a whole path segment, and the
 * full address goes in the title. Two hundred characters of query string help
 * nobody read a message.
 */
export function shortenUrl(text: string, max = 48): string {
  if (text.length <= max) return text;
  const slash = text.indexOf('/');
  const host = slash === -1 ? text : text.slice(0, slash);
  if (host.length >= max - 1) return `${host.slice(0, max - 1)}…`;
  let out = host;
  for (const part of text.slice(host.length).split('/').slice(1)) {
    const next = `${out}/${part}`;
    if (next.length > max) break;
    out = next;
  }
  return out === host ? `${text.slice(0, max)}…` : `${out}/…`;
}

/** Only these schemes ever reach an href, so `javascript:` cannot. */
function safeHref(href: string): string | null {
  const clean = href.replace(UNSAFE_CHARS, '');
  return /^(?:https?:\/\/|mailto:|tel:\+)/i.test(clean) ? clean : null;
}

/** The label a link shows: no scheme, no `www.`, no percent escapes that mean nothing. */
function label(raw: string, kind: LinkKind, max: number): string {
  if (kind === 'phone' || kind === 'email') return raw;
  const bare = decodeUnreserved(
    raw.replace(/^[Hh][Tt][Tt][Pp][Ss]?:\/\//, '').replace(/^www\./, '')
  ).replace(/\/$/, '');
  return shortenUrl(bare, max);
}

/**
 * Every link in a string, in order, with nothing overlapping. The caller walks
 * the string and swaps each match for one atom.
 *
 * @param maxLabel longest visible label before the middle of a URL is dropped.
 */
export function findLinks(text: string, maxLabel = 48): LinkMatch[] {
  const out: LinkMatch[] = [];
  LINK.lastIndex = 0;
  for (let m = LINK.exec(text); m; m = LINK.exec(text)) {
    const g: Record<string, string | undefined> = m.groups ?? {};
    let raw = m[0];
    let kind: LinkKind;
    let href: string | null;

    if (g.phone) {
      kind = 'phone';
      href = safeHref(`tel:+${raw.replace(/\D/g, '')}`);
    } else if (g.email) {
      kind = 'email';
      href = safeHref(`mailto:${raw.replace(UNSAFE_CHARS, '')}`);
    } else {
      kind = 'url';
      raw = trimTrailingPunctuation(raw);
      href = raw.includes('.') ? safeHref(g.url ? raw : `https://${raw}`) : null;
    }

    if (href) {
      const clean = raw.replace(UNSAFE_CHARS, '');
      out.push({
        kind,
        index: m.index,
        raw,
        href,
        text: label(clean, kind, maxLabel),
        title: kind === 'url' ? clean : href
      });
    }
    // Trailing punctuation handed back to the sentence must not be skipped over,
    // and a zero-length step would spin forever.
    LINK.lastIndex = m.index + Math.max(raw.length, 1);
  }
  return out;
}
